<template><div><h2 id="resetting-a-list" tabindex="-1" class="d-docsite--header-2">Resetting a List <a class="header-anchor d-link d-docsite--link d-link" href="#resetting-a-list" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-ls-reset</code> to reset the margin, padding, and list-style-type of a list. Reseting a list applies to the parent <code v-pre>ol</code> or <code v-pre>ul</code>, any child <code v-pre>li</code> elements, and any child <code v-pre>ol</code> or <code v-pre>ul</code> elements.</p>
<code-example custom class="d-d-flex d-jc-center d-fd-column d-p-300 d-bgc-secondary d-w100p d-hmn102" source-code='&lt;ul class=&quot;d-ls-reset&quot;&gt;
  &lt;li&gt;An unordered list item&lt;/li&gt;
  &lt;li&gt;
    An unordered list item
    &lt;ol&gt;
      &lt;li&gt;A nested ordered list item&lt;/li&gt;
      &lt;li&gt;A nested ordered list item&lt;/li&gt;
    &lt;/ol&gt;
  &lt;/li&gt;
  &lt;li&gt;An unordered list item&lt;/li&gt;
  &lt;li&gt;
    An unordered list item
    &lt;ul&gt;
      &lt;li&gt;A nested unordered list item&lt;/li&gt;
      &lt;li&gt;A nested unordered list item&lt;/li&gt;
    &lt;/ul&gt;
  &lt;/li&gt;
&lt;/ul&gt;'>
<ul class="d-ls-reset">
  <li>An unordered list item</li>
  <li>
    An unordered list item
    <ol>
      <li>A nested ordered list item</li>
      <li>A nested ordered list item</li>
    </ol>
  </li>
  <li>An unordered list item</li>
  <li>
    An unordered list item
    <ul>
      <li>A nested unordered list item</li>
      <li>A nested unordered list item</li>
    </ul>
  </li>
</ul>
</code-example>
<h2 id="changing-the-list-style-type" tabindex="-1" class="d-docsite--header-2">Changing the List Style Type <a class="header-anchor d-link d-docsite--link d-link" href="#changing-the-list-style-type" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-lst-{none|disc|circle|decimal|content|none}</code> to change a list item's bullet styling.</p>
<code-example custom class="d-d-flex d-jc-center d-fd-column d-p-300 d-bgc-secondary d-w100p d-hmn102" source-code='&lt;dt-stack gap=&quot;100&quot;&gt;
  &lt;p&gt;Paragraph of text.&lt;/p&gt;
  &lt;ul class=&quot;d-pis-200&quot;&gt;
    &lt;li class=&quot;d-lst-disc&quot;&gt;&lt;strong&gt;disc&lt;/strong&gt; list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-circle&quot;&gt;&lt;strong&gt;circle&lt;/strong&gt; list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;&lt;strong&gt;decimal&lt;/strong&gt; list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-content&quot; style=&quot;--ls-content: &#39;🫠&#39;&quot;&gt;&lt;strong&gt;content&lt;/strong&gt; list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-none&quot;&gt;&lt;strong&gt;none&lt;/strong&gt; list item&lt;/li&gt;
  &lt;/ul&gt;
  &lt;p&gt;Paragraph of text.&lt;/p&gt;
  &lt;ul class=&quot;d-pis-200&quot;&gt;
    &lt;li class=&quot;d-lst-disc&quot;&gt;
      An unordered list item
      &lt;ul class=&quot;d-pis-200&quot;&gt;
        &lt;li class=&quot;d-lst-circle&quot;&gt;A nested unordered list item&lt;/li&gt;
        &lt;li class=&quot;d-lst-circle&quot;&gt;A nested unordered list item&lt;/li&gt;
      &lt;/ul&gt;
    &lt;/li&gt;
    &lt;li class=&quot;d-lst-disc&quot;&gt;
      An unordered list item
      &lt;ul class=&quot;d-pis-200&quot;&gt;
        &lt;li class=&quot;d-pis-100 d-lst-content&quot; style=&quot;--ls-content: &#39;✅&#39;&quot;&gt;A nested unordered list item&lt;/li&gt;
        &lt;li class=&quot;d-pis-100 d-lst-content&quot; style=&quot;--ls-content: &#39;❌&#39;&quot;&gt;A nested unordered list item&lt;/li&gt;
      &lt;/ul&gt;
    &lt;/li&gt;
  &lt;/ul&gt;
&lt;/dt-stack&gt;'>
<dt-stack gap="100">
  <p>Paragraph of text.</p>
  <ul class="d-pis-200">
    <li class="d-lst-disc"><strong>disc</strong> list item</li>
    <li class="d-lst-circle"><strong>circle</strong> list item</li>
    <li class="d-lst-decimal"><strong>decimal</strong> list item</li>
    <li class="d-lst-content" style="--ls-content: '🫠'"><strong>content</strong> list item</li>
    <li class="d-lst-none"><strong>none</strong> list item</li>
  </ul>
  <p>Paragraph of text.</p>
  <ul class="d-pis-200">
    <li class="d-lst-disc">
      An unordered list item
      <ul class="d-pis-200">
        <li class="d-lst-circle">A nested unordered list item</li>
        <li class="d-lst-circle">A nested unordered list item</li>
      </ul>
    </li>
    <li class="d-lst-disc">
      An unordered list item
      <ul class="d-pis-200">
        <li class="d-pis-100 d-lst-content" style="--ls-content: '✅'">A nested unordered list item</li>
        <li class="d-pis-100 d-lst-content" style="--ls-content: '❌'">A nested unordered list item</li>
      </ul>
    </li>
  </ul>
</dt-stack>
</code-example>
<h2 id="custom-starting-number" tabindex="-1" class="d-docsite--header-2">Custom Starting Number <a class="header-anchor d-link d-docsite--link d-link" href="#custom-starting-number" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use the <code v-pre>start</code> attribute for an <code v-pre>&lt;ol&gt;</code> to set its starting number.</p>
<code-example custom class="d-d-flex d-jc-center d-fd-column d-p-300 d-bgc-secondary d-w100p d-hmn102" source-code='&lt;dt-stack gap=&quot;100&quot;&gt;
  &lt;p&gt;Paragraph of text.&lt;/p&gt;
  &lt;ol class=&quot;d-pis-300&quot;&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;Decimal list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;Decimal list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;Decimal list item&lt;/li&gt;
  &lt;/ol&gt;
  &lt;p&gt;Paragraph of text. The list below starts at 4.&lt;/p&gt;
  &lt;ol class=&quot;d-pis-300&quot; start=&quot;4&quot;&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;Decimal list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;Decimal list item&lt;/li&gt;
    &lt;li class=&quot;d-lst-decimal&quot;&gt;Decimal list item&lt;/li&gt;
  &lt;/ol&gt;
&lt;/dt-stack&gt;'>
<dt-stack gap="100">
  <p>Paragraph of text.</p>
  <ol class="d-pis-300">
    <li class="d-lst-decimal">Decimal list item</li>
    <li class="d-lst-decimal">Decimal list item</li>
    <li class="d-lst-decimal">Decimal list item</li>
  </ol>
  <p>Paragraph of text. The list below starts at 4.</p>
  <ol class="d-pis-300" start="4">
    <li class="d-lst-decimal">Decimal list item</li>
    <li class="d-lst-decimal">Decimal list item</li>
    <li class="d-lst-decimal">Decimal list item</li>
  </ol>
</dt-stack>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<utility-class-table>
  <template #content>
    <tbody>
      <tr>
        <th scope="row" class="d-code--sm d-docsite-code">.d-ls-reset</th>
        <td class="d-code--sm">
          margin: 0;<br/>
          padding: 0;<br/>
          list-style: none !important;
        </td>
      </tr>
      <tr>
        <th scope="row" class="d-code--sm d-docsite-code">.d-ls-none</th>
        <td class="d-code--sm">list-style: none !important;</td>
      </tr>
      <tr v-for="i in lists">
        <th scope="row" class="d-code--sm d-docsite-code">.d-lst-{{ i }}</th>
        <td class="d-code--sm">
          <span v-if="i === 'content'">list-style-type: var(--ls-content) !important;</span>
          <span v-else>list-style-type: {{ i }} !important;</span>
        </td>
      </tr>
    </tbody>
  </template>
</utility-class-table>
</div></template>


<script setup>
  import { lists } from '@data/type.json';
</script>